import type { SearchCategory, SearchSuggestion, Product } from '@/types/searchTypes'
import { useLanguageStore } from '~/store/language'
import type { SearchBarSuggestionsResponse } from '~/types/search/searchBar/SearchBarSuggestionsTypes'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'
import { CATEGORY_ID_KEY } from './constants/search'

interface IUseSearchSuggestionsProps {
  searchQuery: Ref<string | undefined>
}

export const useSearchSuggestions = (props: IUseSearchSuggestionsProps) => {
  const products = ref<Product[]>([])
  const manufacturers = ref<string[]>([])
  const suggestions = ref<SearchSuggestion[]>([])
  const categories = ref<SearchCategory[]>([])
  const totalProducts = ref<number>()
  const suggestionsQueryKey = ref<string>()
  const itemsQueryKey = ref<string>()
  const locale = useState<string>('locale.setting')
  const searchCalled = ref<boolean>(false)

  const computedQuery = computed(() => locale.value + props.searchQuery.value)

  const stopWatchDebounced = watchDebounced(
    (): [string] => [computedQuery.value],
    async () => {
      try {
        suggestions.value = []
        categories.value = []
        products.value = []
        manufacturers.value = []
        totalProducts.value = undefined
        suggestionsQueryKey.value = undefined
        itemsQueryKey.value = undefined
        searchCalled.value = false

        if (!props.searchQuery.value || props.searchQuery.value.trim().length < 3) {
          return
        }

        const searchBarSuggestions = await $fetch<SearchBarSuggestionsResponse>('/api/search/getSearchBarSuggestions', {
          method: 'POST',
          body: {
            searchText: props.searchQuery.value.trim(),
            languageId: useLanguageStore().getLanguageId,
          },
          ...fetchRetryParameters,
          headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
        })

        suggestions.value = searchBarSuggestions.suggestedTexts

        const baseSuggestion = searchBarSuggestions.suggestedTexts?.[0]

        if (baseSuggestion?.facets) {
          categories.value = baseSuggestion.facets?.[CATEGORY_ID_KEY]?.slice(0, 5)
        }

        products.value = searchBarSuggestions.suggestedItems.items
        totalProducts.value = searchBarSuggestions.suggestedItems.total

        for (const product of products.value) {
          manufacturers.value.push(product.manufacturer)
        }

        if (manufacturers.value.length) {
          manufacturers.value = [...new Set(manufacturers.value)]
        }
        searchCalled.value = true

        suggestionsQueryKey.value = searchBarSuggestions.suggestionsQueryKey
        itemsQueryKey.value = searchBarSuggestions.itemsQueryKey
      } catch (error) {
        console.error(error)
      }
    },
    { debounce: 500 }
  )

  onBeforeUnmount(() => {
    stopWatchDebounced()
  })

  return {
    products,
    manufacturers,
    suggestions,
    categories,
    totalProducts,
    suggestionsQueryKey,
    itemsQueryKey,
    searchCalled,
  }
}
